import { useCallback, useState } from "react";
import { AssetRow } from "./asset-row";
import { CommunicationRow } from "./communication-row";
import { FeedList } from "./feed-list";
import { SurveyRow } from "./survey-row";

import type { PagedFeed } from "../../hooks/use-paged-feed";
import type { FeedItem, RecentlyEditedType } from "../../hooks/use-recently-edited";
import type { FC, ReactNode } from "react";

const ItemRow: FC<{
  item: FeedItem;
  onDuplicated: (copy: FeedItem) => void;
  onArchived: () => void;
}> = ({ item, onDuplicated, onArchived }) => {
  switch (item.entity_type) {
    case "communication":
      return <CommunicationRow communication={item} onArchived={onArchived} />;
    case "survey":
      return <SurveyRow survey={item} onArchived={onArchived} />;
    case "digital_asset":
      return <AssetRow asset={item} onDuplicated={onDuplicated} onArchived={onArchived} />;
    default:
      return null;
  }
};

/** Communications, Surveys and Digital Assets as the dashboard's rows, a page at a time. */
export const ContentFeed: FC<{
  feed: PagedFeed<FeedItem>;
  /** Something of this kind was archived or duplicated here. */
  onChanged: (entityType: RecentlyEditedType) => void;
  empty: ReactNode;
  errorMessage: string;
  /** Prefixes the buttons' analytics ids. */
  analyticsId: string;
}> = ({ feed, onChanged, empty, errorMessage, analyticsId }) => {
  const { prepend } = feed;

  // Copies made here slide open at the top; everything else just appears.
  const [sliding, setSliding] = useState<ReadonlySet<string>>(new Set());
  const onDuplicated = useCallback(
    (copy: FeedItem) => {
      setSliding((ids) => new Set(ids).add(copy.id));
      prepend(copy);
      onChanged(copy.entity_type);
    },
    [prepend, onChanged],
  );
  const slid = useCallback(
    (id: string) =>
      setSliding((ids) => {
        const rest = new Set(ids);
        rest.delete(id);
        return rest;
      }),
    [],
  );
  const onArchived = useCallback(
    ({ entity_type }: FeedItem) => onChanged(entity_type),
    [onChanged],
  );

  return (
    <FeedList
      feed={feed}
      renderRow={(item, archived) => (
        <ItemRow item={item} onDuplicated={onDuplicated} onArchived={archived} />
      )}
      onArchived={onArchived}
      empty={empty}
      errorMessage={errorMessage}
      analyticsId={analyticsId}
      sliding={sliding}
      onSlid={slid}
    />
  );
};
